import { useState } from 'react';
import { Plus, MessageSquare, Archive, Trash2, Edit3, Search } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { ScrollArea } from '@/components/ui/scroll-area';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { callRpc } from '@/lib/supabaseTyped';
import { toast } from '@/hooks/use-toast';
import {
  useConversations,
  useConversationMutations,
  type ConversationRow,
  type ConversationScope,
} from '@/hooks/useConversations';

export interface ChatSidebarProps {
  scope: ConversationScope;
  activeConversationId: string | null;
  onSelectConversation: (id: string) => void;
  onNewConversation: () => void;
}

function formatRelative(iso: string): string {
  const date = new Date(iso);
  const diff = Date.now() - date.getTime();
  const min = diff / 60000;
  if (min < 1) return 'nu';
  if (min < 60) return `${Math.floor(min)} min`;
  if (min < 60 * 24) return `${Math.floor(min / 60)} h`;
  if (min < 60 * 24 * 7) return `${Math.floor(min / (60 * 24))} d`;
  return date.toLocaleDateString();
}

export function ChatSidebar({
  scope, activeConversationId, onSelectConversation, onNewConversation,
}: ChatSidebarProps) {
  const { data: conversations, isLoading } = useConversations(scope);
  const { softDeleteConversation } = useConversationMutations();
  const [filter, setFilter] = useState('');
  const [renamingId, setRenamingId] = useState<string | null>(null);
  const [renameValue, setRenameValue] = useState('');

  const filtered = (conversations ?? []).filter((c) =>
    c.title.toLowerCase().includes(filter.toLowerCase()),
  );

  const commitRename = async (id: string) => {
    const v = renameValue.trim();
    if (v.length === 0 || v.length > 120) {
      setRenamingId(null);
      return;
    }
    const { data, error } = await callRpc<{ success: boolean; error?: string }>(
      'rename_conversation',
      { p_conversation_id: id, p_new_title: v },
    );
    if (error || !data?.success) {
      toast({
        title: 'Kunde inte byta namn',
        description: error?.message ?? data?.error,
        variant: 'destructive',
      });
    }
    setRenamingId(null);
  };

  const archive = async (id: string) => {
    const { data, error } = await callRpc<{ success: boolean; error?: string }>(
      'archive_conversation',
      { p_conversation_id: id, p_archive: true },
    );
    if (error || !data?.success) {
      toast({ title: 'Archive failed', variant: 'destructive' });
    } else {
      toast({ title: 'Arkiverad' });
    }
  };

  const remove = async (id: string) => {
    const res = await softDeleteConversation(id);
    if (res.success) {
      toast({
        title: 'Raderad',
        description: 'Permanent borttagen efter 7 dagar. Ingen undo.',
      });
    } else {
      toast({ title: 'Kunde inte radera', variant: 'destructive' });
    }
  };

  return (
    <div className="flex flex-col h-full border-r bg-muted/20" data-testid="chat-sidebar">
      <div className="p-2 border-b space-y-2">
        <Button
          onClick={onNewConversation}
          size="sm"
          className="w-full gap-1"
          data-testid="new-conversation-btn"
        >
          <Plus className="w-3.5 h-3.5" />
          Ny chat
        </Button>
        <div className="relative">
          <Search className="w-3 h-3 absolute left-2 top-1/2 -translate-y-1/2 text-muted-foreground" />
          <Input
            value={filter}
            onChange={(e) => setFilter(e.target.value)}
            placeholder="Sök..."
            className="h-7 text-xs pl-7"
          />
        </div>
      </div>
      <ScrollArea className="flex-1">
        <div className="p-1 space-y-0.5">
          {isLoading && (
            <div className="text-xs text-muted-foreground p-2 text-center">
              Laddar…
            </div>
          )}
          {!isLoading && filtered.length === 0 && (
            <div className="text-xs text-muted-foreground p-2 text-center">
              {filter ? 'Inga träffar' : 'Inga chattar än'}
            </div>
          )}
          {filtered.map((c) => (
            <ChatSidebarRow
              key={c.id}
              conversation={c}
              active={c.id === activeConversationId}
              renaming={renamingId === c.id}
              renameValue={renameValue}
              onSelect={() => onSelectConversation(c.id)}
              onStartRename={() => {
                setRenamingId(c.id);
                setRenameValue(c.title);
              }}
              onRenameChange={setRenameValue}
              onCommitRename={() => commitRename(c.id)}
              onCancelRename={() => setRenamingId(null)}
              onArchive={() => archive(c.id)}
              onDelete={() => remove(c.id)}
            />
          ))}
        </div>
      </ScrollArea>
    </div>
  );
}

interface ChatSidebarRowProps {
  conversation: ConversationRow;
  active: boolean;
  renaming: boolean;
  renameValue: string;
  onSelect: () => void;
  onStartRename: () => void;
  onRenameChange: (v: string) => void;
  onCommitRename: () => void;
  onCancelRename: () => void;
  onArchive: () => void;
  onDelete: () => void;
}

function ChatSidebarRow({
  conversation, active, renaming, renameValue,
  onSelect, onStartRename, onRenameChange, onCommitRename, onCancelRename,
  onArchive, onDelete,
}: ChatSidebarRowProps) {
  return (
    <div
      className={`group rounded-md flex items-center gap-1 pr-1 ${
        active ? 'bg-primary/10' : 'hover:bg-accent/50'
      }`}
    >
      {renaming ? (
        <Input
          autoFocus
          value={renameValue}
          onChange={(e) => onRenameChange(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') onCommitRename();
            if (e.key === 'Escape') onCancelRename();
          }}
          onBlur={onCommitRename}
          className="h-7 text-xs flex-1 mx-1"
        />
      ) : (
        <button
          type="button"
          onClick={onSelect}
          onDoubleClick={onStartRename}
          className="flex-1 text-left px-2 py-1.5 flex items-center gap-1.5 overflow-hidden"
          title={conversation.title}
          data-testid={`chat-row-${conversation.id}`}
        >
          <MessageSquare className="w-3.5 h-3.5 flex-shrink-0 text-muted-foreground" />
          <div className="flex-1 overflow-hidden">
            <div className="text-xs font-medium truncate">{conversation.title}</div>
            <div className="text-[10px] text-muted-foreground">
              {conversation.message_count} meddelanden · {formatRelative(conversation.last_activity_at)}
            </div>
          </div>
        </button>
      )}
      {!renaming && (
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button
              size="icon"
              variant="ghost"
              className="h-6 w-6 opacity-0 group-hover:opacity-100"
            >
              <Edit3 className="w-3 h-3" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end">
            <DropdownMenuItem onClick={onStartRename}>
              <Edit3 className="w-3.5 h-3.5 mr-2" /> Byt namn
            </DropdownMenuItem>
            <DropdownMenuItem onClick={onArchive}>
              <Archive className="w-3.5 h-3.5 mr-2" /> Arkivera
            </DropdownMenuItem>
            <DropdownMenuItem onClick={onDelete} className="text-destructive">
              <Trash2 className="w-3.5 h-3.5 mr-2" /> Radera
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      )}
    </div>
  );
}
